<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <ul class="nav nav-pills customer-report-tabs">
          <li class="nav-item" v-for="tab in tabs" :key="tab.key">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeTab === tab.key }"
              @click="$emit('tab-change', tab.key)"
            >
              {{ tab.label }}
            </button>
          </li>
        </ul>

        <div class="d-flex flex-wrap align-items-center gap-2">
          <span class="summary-chip">
            {{ $t(`${i18nScope}.summary_total`) }}: ${{ formatAmount(summary.grandTotal) }}
          </span>
          <span class="summary-chip chip-success">
            {{ $t(`${i18nScope}.summary_paid`) }}: ${{ formatAmount(summary.paid) }}
          </span>
          <span class="summary-chip chip-danger">
            {{ $t(`${i18nScope}.summary_due`) }}: ${{ formatAmount(summary.due) }}
          </span>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="items"
        :row-key="rowKey"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 980 }"
        class="text-nowrap align-middle customer-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ emptyText }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="getStatusClass(record.status)">
              {{ formatLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'invoice_total'">
            ${{ formatAmount(record.invoice_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            ${{ formatAmount(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            ${{ formatAmount(record.due) }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { CustomerReportItem } from "@/services/report";

type CustomerReportTab = "sale" | "quotation";

export default defineComponent({
  name: "CustomerReport",
  props: {
    activeTab: {
      type: String as () => CustomerReportTab,
      required: true,
    },
    items: {
      type: Array as () => CustomerReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    summary: {
      type: Object as () => { grandTotal: number; paid: number; due: number },
      required: true,
    },
    i18nScope: {
      type: String,
      default: "customer_report_page",
    },
  },
  emits: ["tab-change", "table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const tabs = computed<Array<{ key: CustomerReportTab; label: string }>>(() => [
      { key: "sale", label: t(`${props.i18nScope}.tab_sales`) },
      { key: "quotation", label: t(`${props.i18nScope}.tab_quotations`) },
    ]);

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 240, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", dataIndex: "due", width: 120, align: "center" as const },
    ]);

    const rowKey = computed(() => (props.activeTab === "quotation" ? "quote_id" : "sale_id"));
    const emptyText = computed(() =>
      props.activeTab === "quotation" ? t(`${props.i18nScope}.no_quotations_found`) : t(`${props.i18nScope}.no_sales_found`)
    );

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const formatLabel = (value?: string | number) => {
      const text = String(value ?? "pending")
        .replace(/_/g, " ")
        .toLowerCase();
      const normalizedKey = text.replace(/\s+/g, "_");
      const translationKey = `${props.i18nScope}.status_${normalizedKey}`;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;

      return text.charAt(0).toUpperCase() + text.slice(1);
    };
    const getStatusClass = (value?: string | number) => {
      const normalized = String(value ?? "pending").toLowerCase();
      if (["received", "completed", "approved", "sent"].includes(normalized)) return "status-success";
      if (["pending", "ordered", "draft"].includes(normalized)) return "status-warning";
      if (["cancelled", "rejected"].includes(normalized)) return "status-danger";
      return "status-neutral";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      tabs,
      columns,
      rowKey,
      emptyText,
      formatDate,
      formatAmount,
      formatLabel,
      getStatusClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.customer-report-tabs {
  gap: 10px;
}

.customer-report-tabs .nav-link {
  border: 1px solid #e6ebf2;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #5b6780;
  background: #f8fafc;
}

.customer-report-tabs .nav-link.active {
  border-color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
  color: #5d5fef;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.1);
}

.chip-success {
  color: #1f9254;
  background: #e8f7ee;
}

.chip-danger {
  color: #d33c43;
  background: #fdecec;
}

.customer-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.customer-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-success {
  color: #1f9254;
  background: #e8f7ee;
}

.status-warning {
  color: #d48a00;
  background: #fff4db;
}

.status-danger {
  color: #d33c43;
  background: #fdecec;
}

.status-neutral {
  color: #61748a;
  background: #eef2f7;
}
</style>
